import classNames from 'classnames';
import { RefObject, useEffect, useRef, useState } from 'react';
import './DaySeparator.styles.scss';

export type tFloatingDayChipProps = {
  /** The conversation's scroll container. */
  scrollerRef: RefObject<HTMLElement>;
};

/** Hidden again after this long without scroll movement, as in WhatsApp Web. */
export const FLOATING_DAY_CHIP_HIDE_MS = 3000;
// A scroll counts as the user's when it follows their own input this closely.
// Opening a chat, loading older messages and jumping to the newest one also
// scroll the thread, and the chip must not flash for those.
const USER_INPUT_WINDOW_MS = 1500;
// A separator this close to the top already says the day, so the chip stays out.
const SEPARATOR_AT_TOP_PX = 40;
const NAVIGATION_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

type tChipState = { label: string; visible: boolean };

/**
 * While the user scrolls the conversation, a chip at the top says which day
 * they are looking at; it fades out 3 s after they stop. It owns its state, so
 * scrolling re-renders only the chip, never the thread.
 */
export default function FloatingDayChip(props: tFloatingDayChipProps) {
  const { scrollerRef } = props;
  const [chip, setChip] = useState<tChipState>({ label: '', visible: false });
  const lastInputAt = useRef(0);
  const pointerDown = useRef(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let frame = 0;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;

    const markInput = () => {
      lastInputAt.current = Date.now();
    };
    const onPointerDown = () => {
      pointerDown.current = true;
      markInput();
    };
    const onPointerUp = () => {
      pointerDown.current = false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (NAVIGATION_KEYS.has(event.key)) markInput();
    };

    const update = () => {
      frame = 0;
      const top = scroller.getBoundingClientRect().top;
      let label = '';
      let separatorAtTop = false;
      scroller.querySelectorAll<HTMLElement>('[data-day-separator]').forEach((separator) => {
        const offset = separator.getBoundingClientRect().top - top;
        if (offset <= 8) label = separator.dataset.label ?? label;
        if (offset > -8 && offset < SEPARATOR_AT_TOP_PX) separatorAtTop = true;
      });
      const next = { label, visible: Boolean(label) && !separatorAtTop };
      setChip((current) =>
        current.label === next.label && current.visible === next.visible ? current : next,
      );
      clearTimeout(hideTimer);
      hideTimer = setTimeout(
        () => setChip((current) => (current.visible ? { ...current, visible: false } : current)),
        FLOATING_DAY_CHIP_HIDE_MS,
      );
    };

    const onScroll = () => {
      const byUser = pointerDown.current || Date.now() - lastInputAt.current < USER_INPUT_WINDOW_MS;
      if (!byUser || frame) return;
      frame = requestAnimationFrame(update);
    };

    scroller.addEventListener('wheel', markInput, { passive: true });
    scroller.addEventListener('touchmove', markInput, { passive: true });
    scroller.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('keydown', onKeyDown);
    scroller.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      scroller.removeEventListener('wheel', markInput);
      scroller.removeEventListener('touchmove', markInput);
      scroller.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('keydown', onKeyDown);
      scroller.removeEventListener('scroll', onScroll);
      clearTimeout(hideTimer);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollerRef]);

  if (!chip.label) return null;

  return (
    <div
      className={classNames('FloatingDayChip', { 'FloatingDayChip--visible': chip.visible })}
      aria-hidden="true"
      data-testid="floating-day-chip"
    >
      <span className="FloatingDayChip__Chip">{chip.label}</span>
    </div>
  );
}
